import {
  h,
  spec,
  list,
  remap,
  rec,
  variant,
  text,
  DOMProperty,
  AttributeStore,
  route
} from 'forest'
import {
  createEvent,
  Store,
  Node,
  is,
  combine,
  createStore,
  sample,
  createApi
} from 'effector'

import {GroupRecord} from '../group'

import {AnyRuntimeUnit, UnitCell} from './index.h'
import {border} from './border'
import {dot} from './dot'
import {computeFullData} from './fullData'
import {CELL} from './env'

const style = {
  page() {
    css`
      margin-top: 0.25rem;
    `
  },
  color: {
    red: 'firebrick',
    black: '#343434',
    gray: '#747474',
    white: 'oldlace'
  },
  arrow: {
    thinMarker() {
      css`
        fill: none;
        stroke: #231f20;
        vector-effect: non-scaling-stroke;
        stroke-width: 2;
      `
    },
    dashedLine() {
      css`
        fill: none;
        stroke: #231f20;
        vector-effect: non-scaling-stroke;
        stroke-width: 2;
        stroke-dasharray: 11, 5;
      `
    }
  }
}

function cellsDots(cells: Store<UnitCell[]>) {
  list({
    source: cells,
    key: 'id',
    fields: ['color', 'tag'],
    fn({store, fields: [color, tag]}) {
      dot({
        pos: store,
        color,
        tag
      })
    }
  })
}

export function Board({
  unitMap,
  filesByDir
}: {
  unitMap: Store<Record<string, AnyRuntimeUnit>>
  filesByDir: Store<GroupRecord<GroupRecord<AnyRuntimeUnit>>[]>
  unitsByFile: Store<GroupRecord<AnyRuntimeUnit>[]>
}) {
  const fullData = combine(unitMap, filesByDir, computeFullData)
  const [dirGroups] = remap(fullData, ['dirGroups'] as const)

  h('h1', {text: 'Board'})
  h('section', {
    fn() {
      style.page()
      const HEIGHT = 6000
      const WIDTH = 2200
      h('svg', {
        attr: {
          width: WIDTH,
          height: HEIGHT,
          viewBox: `0 0 ${WIDTH} ${HEIGHT}`,
          preserveAspectRatio: 'xMidYMid'
        },
        fn() {
          h('defs', () => {
            h('marker', {
              attr: {
                id: 'thinMarker',
                markerWidth: 10,
                markerHeight: 8,
                refX: 9.5,
                refY: 5.1,
                orient: -45,
                markerUnits: 'userSpaceOnUse'
              },
              fn() {
                style.arrow.thinMarker()
              }
            })
          })
          list({
            source: dirGroups,
            key: 'dir',
            fields: [
              'fileGroups',
              'borderSize',
              'dir',
              'inputs',
              'outputs',
              'lines'
            ],
            fn({
              fields: [fileGroups, borderSize, dir, inputs, outputs, lines]
            }) {
              h('g', {
                data: {dir},
                fn() {
                  border({
                    borderSize,
                    color: style.color.black,
                    dataTag: dir
                  })
                  cellsDots(inputs)
                  list({
                    source: fileGroups,
                    key: 'file',
                    fields: ['cells', 'borderSize', 'file'],
                    fn({fields: [cells, borderSize, file]}) {
                      border({
                        borderSize,
                        color: style.color.black,
                        dataTag: file
                      })
                      cellsDots(cells)
                    }
                  })
                  cellsDots(outputs)
                  list(lines, ({store}) => {
                    const x1 = store.map(line => {
                      if (!line[0] || !line[1]) {
                        console.warn(line)
                      }
                      const [{x}] = line
                      return (x + 0.5) * CELL
                    })
                    const y1 = store.map(([{y}]) => (y + 0.5) * CELL)
                    const x2 = store.map(([, {x}]) => (x + 0.5) * CELL)
                    const y2 = store.map(([, {y}]) => (y + 0.5) * CELL)
                    h('line', {
                      attr: {
                        x1,
                        y1,
                        x2,
                        y2,
                        stroke: style.color.black,
                        'stroke-width': '1'
                      }
                    })
                  })
                }
              })
            }
          })
        }
      })
    }
  })
}

declare function css(
  words: TemplateStringsArray,
  ...values: Array<DOMProperty | AttributeStore>
): void
declare function createGlobalStyle(
  words: TemplateStringsArray,
  ...values: Array<DOMProperty | AttributeStore>
): void
